import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DropdownProps {
  /** `triggerProps` wires up disclosure semantics; `open` is for styling only. */
  renderTrigger: (arg: {
    open: boolean;
    triggerProps: {
      type: 'button';
      onClick: () => void;
      'aria-expanded': boolean;
      'aria-haspopup': 'menu';
    };
  }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
  panelClassName?: string;
}

/**
 * Disclosure menu: closes on Esc and on outside click, restores focus to its
 * trigger, and reports state through aria-expanded.
 */
export function Dropdown({ renderTrigger, children, align = 'end', className, panelClassName }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const container = containerRef.current;

    const onPointerDown = (e: MouseEvent) => {
      // Clicking elsewhere closes the menu but leaves focus where the user put it.
      if (!container?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        // Dismissing with the keyboard returns focus to the trigger.
        container?.querySelector<HTMLElement>('button')?.focus();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {renderTrigger({
        open,
        triggerProps: {
          type: 'button',
          onClick: () => setOpen((v) => !v),
          'aria-expanded': open,
          'aria-haspopup': 'menu',
        },
      })}

      {open && (
        <div
          role="menu"
          className={cn(
            'animate-fade-in absolute top-[calc(100%+6px)] z-50 min-w-52 rounded-lg border border-line bg-surface-3 p-1 shadow-popover',
            align === 'end' ? 'right-0' : 'left-0',
            panelClassName
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}

interface MenuItemProps {
  icon?: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
  danger?: boolean;
  children: ReactNode;
}

export function MenuItem({ icon: Icon, onClick, danger, children }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
        danger ? 'text-danger hover:bg-danger/10' : 'text-fg-muted hover:bg-surface-2 hover:text-fg'
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
      {children}
    </button>
  );
}
